/**
 * 読み上げ用の文分割（ADR-0027）。声の端末（apps/voice-client/src/text.ts）と同じ規則。
 * PWA は Node の部品を取り込めない（ブラウザ向けの別の束）ので写しを置き、同じ結果になることを speech.test.ts で確かめる。
 */

/** trace_id のような機械的な行は読まない。記号だけの切れ端は捨てる */
export function splitSentences(text: string): string[] {
  return text
    .split("\n")
    .filter((line) => !/^\s*trace_id:/i.test(line))
    .join("\n")
    .split(/(?<=[。．！？!?])|\n+/)
    .map(cleanForSpeech)
    .filter((s) => /[\p{L}\p{N}]/u.test(s));
}

/** 画面向けの飾り（太字の記号、見出し、箇条書きの印）を読み上げから外す */
export function cleanForSpeech(s: string): string {
  return s
    .replace(/[*`]/g, "")
    .replace(/^\s*#{1,6}\s+/, "")
    .replace(/^\s*(?:[-・]|\d+[.)])\s+/, "")
    .trim();
}

/** 少しずつ届く返事（累積テキスト）から、文末まで揃った文を順に取り出す（最初の文を早く鳴らすため） */
export class SentenceCutter {
  private acc = "";
  private consumed = 0;

  push(accumulated: string): string[] {
    this.acc = accumulated;
    const rest = accumulated.slice(this.consumed);
    let end = -1;
    for (const m of rest.matchAll(/[。．！？!?\n]+/g)) end = m.index + m[0].length;
    if (end < 0) return [];
    this.consumed += end;
    return splitSentences(rest.slice(0, end));
  }

  finish(finalText: string): string[] {
    const spoken = this.acc.slice(0, this.consumed);
    const rest = finalText.startsWith(spoken)
      ? finalText.slice(this.consumed)
      : this.consumed === 0
        ? finalText
        : this.acc.slice(this.consumed);
    this.consumed = Number.MAX_SAFE_INTEGER;
    return splitSentences(rest);
  }
}

/** 文と、返事の文（累積テキスト）の中の文字の範囲（コードポイントの位置。end は含まない。割り込みの報告に使う。ADR-0042） */
export interface RangedSentence {
  text: string;
  start: number;
  end: number;
}

/**
 * splitSentences と同じ切り方で、元の文の中の範囲（コードポイント）も返す。base は text の先頭の位置。
 * 文の中身（text）は splitSentences と同じになる（speech.test.ts で確かめる）
 */
export function splitSentencesWithRanges(text: string, base = 0): RangedSentence[] {
  const out: RangedSentence[] = [];
  const chars = Array.from(text);
  let start = 0;
  let buf = "";
  const flush = (end: number) => {
    const raw = buf;
    buf = "";
    if (/^\s*trace_id:/i.test(raw)) return;
    const cleaned = cleanForSpeech(raw);
    if (/[\p{L}\p{N}]/u.test(cleaned))
      out.push({ text: cleaned, start: base + start, end: base + end });
  };
  for (let i = 0; i < chars.length; i++) {
    const c = chars[i] as string;
    if (c === "\n") {
      flush(i);
      while (chars[i + 1] === "\n") i++;
      start = i + 1;
      continue;
    }
    buf += c;
    if ("。．！？!?".includes(c)) {
      flush(i + 1);
      start = i + 1;
    }
  }
  if (buf) flush(chars.length);
  return out;
}

/** SentenceCutter の、範囲つきの版（読み上げの位置を割り込みの報告に入れるため。ADR-0042） */
export class RangedSentenceCutter {
  private acc = "";
  private consumed = 0;

  push(accumulated: string): RangedSentence[] {
    this.acc = accumulated;
    const rest = accumulated.slice(this.consumed);
    let end = -1;
    for (const m of rest.matchAll(/[。．！？!?\n]+/g)) end = m.index + m[0].length;
    if (end < 0) return [];
    const base = Array.from(accumulated.slice(0, this.consumed)).length;
    this.consumed += end;
    return splitSentencesWithRanges(rest.slice(0, end), base);
  }

  finish(finalText: string): RangedSentence[] {
    const spoken = this.acc.slice(0, this.consumed);
    // SentenceCutter.finish と同じ: 流した文の続きなら残りを、違えば（まだ何も読んでいなければ）全文を、それ以外は手元の残りを
    const text = finalText.startsWith(spoken) || this.consumed === 0 ? finalText : this.acc;
    const from = this.consumed === 0 ? 0 : this.consumed;
    const base = Array.from(text.slice(0, from)).length;
    this.consumed = Number.MAX_SAFE_INTEGER;
    return splitSentencesWithRanges(text.slice(from), base);
  }
}
